import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["preview", "form", "webPreview", "pdfPreview", "webButton", "pdfButton", "webIframe"]
  static values = { 
    profileId: String,
    previewUrl: String,
    previewMethod: String
  }

  connect() {
    // Auto-refresh preview every 2 seconds if form has changes
    this.startAutoRefresh()
    // Set initial state
    this.currentPreview = 'web'
    // Update button states only if buttons are present
    if (this.hasWebButtonTarget && this.hasPdfButtonTarget) {
      this.updateButtonStates()
    }
    
    // Listen for profile saved event
    this.handleProfileSavedBound = this.handleProfileSaved.bind(this)
    document.addEventListener('profile:saved', this.handleProfileSavedBound)
    
    // Listen for competencies updated event (für alle Änderungen: Position, Highlight, Aktiv/Inaktiv)
    this.handleCompetenciesUpdatedBound = this.handleCompetenciesUpdated.bind(this)
    document.addEventListener('profile:competencies-updated', this.handleCompetenciesUpdatedBound)
    
    // Listen for competencies reordered event (Legacy, für Rückwärtskompatibilität)
    this.handleCompetenciesReorderedBound = this.handleCompetenciesReordered.bind(this)
    document.addEventListener('profile:competencies-reordered', this.handleCompetenciesReorderedBound)
  }
  
  disconnect() {
    this.stopAutoRefresh()
    document.removeEventListener('profile:saved', this.handleProfileSavedBound)
    document.removeEventListener('profile:competencies-updated', this.handleCompetenciesUpdatedBound)
    document.removeEventListener('profile:competencies-reordered', this.handleCompetenciesReorderedBound)
  }
  
  handleProfileSaved(event) {
    // Reload preview when profile is saved
    if (this.currentPreview === 'web') {
      this.reloadPreview()
    }
  }

  handleCompetenciesUpdated(event) {
    // Reload preview when competencies are updated (Position, Highlight, Aktiv/Inaktiv)
    if (this.currentPreview === 'web' && this.hasWebPreviewTarget) {
      this.reloadPreview()
    }
  }

  handleCompetenciesReordered(event) {
    // Legacy: Reload preview when competencies are reordered
    if (this.currentPreview === 'web') {
      this.reloadPreview()
    }
  }

  startAutoRefresh() {
    this.refreshInterval = setInterval(() => {
      if (this.hasFormTarget && this.formHasChanges()) {
        this.refreshPreview()
      }
    }, 2000)
  }

  stopAutoRefresh() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  }

  formHasChanges() {
    // Check if any form field has been modified
    if (!this.hasFormTarget) {
      return false
    }
    
    const form = this.formTarget
    if (!form || !(form instanceof HTMLFormElement)) {
      return false
    }
    
    const formData = new FormData(form)
    const currentData = Array.from(formData.entries()).map(([key, value]) => `${key}=${value}`).join('&')
    
    if (!this.lastFormData) {
      this.lastFormData = currentData
      return false
    }
    
    const hasChanges = this.lastFormData !== currentData
    this.lastFormData = currentData
    return hasChanges
  }

  async refreshPreview() {
    // Only refresh web preview if it's currently visible
    if (this.currentPreview !== 'web') {
      return
    }

    if (!this.hasFormTarget) {
      return
    }

    try {
      const form = this.formTarget
      if (!form || !(form instanceof HTMLFormElement)) {
        return
      }
      
      const formData = new FormData(form)
      
      // Add a flag to indicate this is a preview request
      formData.append('preview', 'true')
      
      const method = this.previewMethodValue || 'POST'
      const previewUrl = this.profileIdValue ? 
        `/backend/profiles/${this.profileIdValue}/preview` : 
        this.previewUrlValue
      
      if (!previewUrl) {
        return
      }
      
      if (!this.hasWebPreviewTarget) {
        return
      }
      
      const iframe = this.webPreviewTarget.querySelector('iframe')
      if (!iframe) {
        return
      }
      
      // Reload iframe with updated form data
      // We need to create a form submission to the iframe
      const formAction = previewUrl
      const formMethod = method
      
      // Create a hidden form and submit it to the iframe
      const hiddenForm = document.createElement('form')
      hiddenForm.method = formMethod
      hiddenForm.action = formAction
      hiddenForm.target = iframe.name || 'profile-preview-iframe'
      hiddenForm.style.display = 'none'
      
      // Copy all form data
      for (const [key, value] of formData.entries()) {
        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = key
        input.value = value
        hiddenForm.appendChild(input)
      }
      
      // Add CSRF token
      const csrfToken = document.querySelector('meta[name="csrf-token"]')
      if (csrfToken) {
        const csrfInput = document.createElement('input')
        csrfInput.type = 'hidden'
        csrfInput.name = 'authenticity_token'
        csrfInput.value = csrfToken.content
        hiddenForm.appendChild(csrfInput)
      }
      
      document.body.appendChild(hiddenForm)
      hiddenForm.submit()
      document.body.removeChild(hiddenForm)
      
      // Alternative: Use iframe.src with query params (simpler but less reliable)
      // iframe.src = previewUrl + '?' + new URLSearchParams(formData).toString()
    } catch (error) {
      console.error('Preview refresh error:', error)
    }
  }

  togglePreview() {
    if (this.previewTarget.classList.contains('hidden')) {
      this.showPreview()
    } else {
      this.hidePreview()
    }
  }

  showPreview() {
    this.previewTarget.classList.remove('hidden')
    this.refreshPreview()
  }

  hidePreview() {
    this.previewTarget.classList.add('hidden')
  }

  showWebPreview() {
    this.currentPreview = 'web'
    if (this.hasWebButtonTarget && this.hasPdfButtonTarget) {
      this.updateButtonStates()
    }
    this.showPreviewPanel('web')
  }

  showPdfPreview() {
    this.currentPreview = 'pdf'
    if (this.hasWebButtonTarget && this.hasPdfButtonTarget) {
      this.updateButtonStates()
    }
    this.showPreviewPanel('pdf')
  }

  showPreviewPanel(type) {
    if (type === 'web') {
      if (this.hasWebPreviewTarget) {
        this.webPreviewTarget.classList.remove('hidden')
      }
      if (this.hasPdfPreviewTarget) {
        this.pdfPreviewTarget.classList.add('hidden')
      }
    } else {
      if (this.hasWebPreviewTarget) {
        this.webPreviewTarget.classList.add('hidden')
      }
      if (this.hasPdfPreviewTarget) {
        this.pdfPreviewTarget.classList.remove('hidden')
      }
    }
  }

  updateButtonStates() {
    // Prüfe ob die Button-Targets vorhanden sind
    if (!this.hasWebButtonTarget || !this.hasPdfButtonTarget) {
      return
    }
    
    if (this.currentPreview === 'web') {
      this.webButtonTarget.className = 'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 bg-white text-blue-600 shadow-sm'
      this.pdfButtonTarget.className = 'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 text-gray-500 hover:text-gray-700'
    } else {
      this.webButtonTarget.className = 'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 text-gray-500 hover:text-gray-700'
      this.pdfButtonTarget.className = 'flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200 bg-white text-red-600 shadow-sm'
    }
  }

  reloadPreview() {
    // Prüfe ob webPreview Target vorhanden ist
    if (!this.hasWebPreviewTarget) {
      return
    }
    
    // Reload the iframe
    const iframe = this.webPreviewTarget.querySelector('iframe')
    if (iframe) {
      // If profile is persisted, reload from profile_path
      if (this.profileIdValue) {
        iframe.src = iframe.src.split('?')[0] + '?t=' + Date.now()
      } else {
        // For new profiles, refresh with form data
        this.refreshPreview()
      }
    }
  }
}
